/**
 * Mini Month Grid for Quick Schedule Dialog
 *
 * A compact calendar grid for selecting processing dates.
 * Future: Will show capacity indicators via pips on each date.
 */
import { ReactNode, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Grid,
  IconButton,
  Paper,
  Popover,
  Stack,
  styled,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  KeyboardArrowDown,
} from '@mui/icons-material';
import moment, { Moment } from 'moment';

import { isGeneratedBlockTitle } from '@farmshare/utils';

import {
  EnumProcessorSchedulingAnimalSpecies,
  useCalculateProcessorCapacityLazyQuery,
} from '../../../lib/graphql';
import { useUserState } from 'providers/user-state-provider';

interface CapacitySpot {
  date: string;
  blockTitle?: string;
  blockIndex?: number;
  spotsRemaining: number;
  isOutsideSchedulingWindow?: boolean;
  outsideWindowNote?: string;
}

const DayCell = styled(Box)(({ theme }) => ({
  aspectRatio: '3 / 2',
  [theme.breakpoints.down('sm')]: {
    aspectRatio: '1 / 1',
  },
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  borderRadius: theme.spacing(0.5),
  transition: 'background-color 0.2s',
  padding: theme.spacing(0.25),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&.selected': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  '&.today': {
    fontWeight: 'bold',
  },
  '&.other-month': {
    color: theme.palette.text.disabled,
    cursor: 'default',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  '&.disabled': {
    color: theme.palette.text.disabled,
    cursor: 'not-allowed',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  '&.low-capacity': {
    opacity: 0.7,
  },
  '&.outside-window': {
    opacity: 0.4,
    cursor: 'default',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
}));

const PipContainer = styled(Box)({
  display: 'flex',
  gap: '2px',
  marginTop: '4px',
  height: '6px',
  justifyContent: 'center',
});

const Pip = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'pipColor' && prop !== 'compact',
})<{ pipColor: string; compact?: boolean }>(({ pipColor, compact }) => ({
  width: compact ? '10px' : '18px',
  height: compact ? '10px' : '18px',
  borderRadius: '50%',
  backgroundColor: pipColor,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.7rem',
  fontWeight: 600,
  color: '#fff',
}));

const LegendItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

const LegendPip = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'pipColor',
})<{ pipColor: string }>(({ pipColor }) => ({
  width: '12px',
  height: '12px',
  borderRadius: '50%',
  backgroundColor: pipColor,
  flexShrink: 0,
}));

interface QuickScheduleMiniMonthProps {
  selectedDate: Moment | null;
  onDateSelect: (date: Moment) => void;
  minDate?: Moment;
  vendorId: string;
  species?: EnumProcessorSchedulingAnimalSpecies;
  selectedQuantity?: number;
  onBlockSelect?: (block: {
    blockTitle: string;
    blockIndex?: number;
    date: string;
  }) => void;
  selectedBlock?: {
    blockTitle?: string;
    blockIndex?: number;
  };
  isProducerView?: boolean;
  /**
   * Optional content rendered inside the day-detail overlay when the opened
   * day has one or more blocks without enough capacity for `selectedQuantity`.
   * Use this to offer a fallback action (e.g. a waitlist CTA).
   */
  renderUnavailableDayContent?: (date: Moment) => ReactNode;
  /**
   * Fired with the month now on screen, including the first one.
   *
   * The month is internal state, but it is also the window the producer is
   * actually shopping in — so a waitlist offer beside the calendar can say
   * "tell me about October" instead of guessing at a range.
   */
  onMonthChange?: (month: Moment) => void;
}

export function QuickScheduleMiniMonth({
  selectedDate,
  onDateSelect,
  minDate = moment().add(1, 'day'),
  vendorId,
  species = EnumProcessorSchedulingAnimalSpecies.Beef,
  selectedQuantity = 1,
  onBlockSelect = undefined,
  selectedBlock = undefined,
  isProducerView = false,
  renderUnavailableDayContent = undefined,
  onMonthChange = undefined,
}: QuickScheduleMiniMonthProps) {
  const [currentMonth, setCurrentMonth] = useState(
    selectedDate?.clone() || moment(),
  );
  const [showBlockSelection, setShowBlockSelection] = useState(false);
  const [selectedDateForBlocks, setSelectedDateForBlocks] =
    useState<Moment | null>(null);
  const [showMonthSelector, setShowMonthSelector] = useState(false);
  const [selectorYear, setSelectorYear] = useState(currentMonth.year());
  const monthLabelRef = useRef<HTMLButtonElement | null>(null);
  const autoSelectedDateRef = useRef<string | null>(null);
  const today = moment().startOf('day');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isProcessor } = useUserState();

  // Load capacity data
  const [calculateProcessorCapacityQuery, { data: capacityData }] =
    useCalculateProcessorCapacityLazyQuery({
      fetchPolicy: 'no-cache',
    });

  // Report the visible month out, including the first one — a caller
  // offering a fallback beside the calendar needs to know which window the
  // producer is looking at, not just which one they end up clicking in.
  useEffect(() => {
    onMonthChange?.(currentMonth.clone());
  }, [currentMonth, onMonthChange]);

  // Fetch capacity when component mounts or month changes
  useEffect(() => {
    if (vendorId) {
      const startDate = currentMonth
        .clone()
        .startOf('month')
        .format('YYYY-MM-DD');
      const endDate = currentMonth
        .clone()
        .endOf('month')
        .add(1, 'week')
        .format('YYYY-MM-DD');
      calculateProcessorCapacityQuery({
        variables: {
          vendorId,
          selectedAnimalSpecies: species,
          startDate,
          endDate,
          dateFormat: 'M/DD/YYYY',
          groupFormat: 'MM/YYYY',
        },
      });
    }
  }, [vendorId, species, currentMonth, calculateProcessorCapacityQuery]);

  // Auto-open block selection overlay if there's already a selected date and block
  // but NOT if we just auto-selected a single block for this specific date
  useEffect(() => {
    const currentDateStr = selectedDate?.format('YYYY-MM-DD');
    const isAutoSelected =
      currentDateStr && autoSelectedDateRef.current === currentDateStr;

    // Only open overlay if both date and block are set, and this wasn't auto-selected
    if (selectedDate && selectedBlock?.blockTitle && !isAutoSelected) {
      setShowBlockSelection(true);
      setSelectedDateForBlocks(selectedDate);
    }

    // Clear the ref only if we've moved to a different date
    if (
      autoSelectedDateRef.current &&
      currentDateStr !== autoSelectedDateRef.current
    ) {
      autoSelectedDateRef.current = null;
    }
  }, [selectedDate, selectedBlock?.blockTitle]);

  // Get available blocks for the selected date
  const getBlocksForDate = (date: Moment | null) => {
    if (!date || !capacityData?.calculateProcessorCapacity?.spotGroups) {
      return [];
    }

    const dateStr = date.format('M/DD/YYYY');
    const { spotGroups } = capacityData.calculateProcessorCapacity;
    const blocks: Array<{
      blockTitle: string;
      blockIndex?: number;
      spotsRemaining: number;
    }> = [];

    // Find all blocks for this date across all groups
    for (const groupKey of Object.keys(spotGroups)) {
      const group = spotGroups[groupKey] as { spots?: CapacitySpot[] };
      const matchingSpots = group.spots?.filter(
        (s: CapacitySpot) => s.date === dateStr,
      );
      if (matchingSpots) {
        matchingSpots.forEach((spot: CapacitySpot) => {
          blocks.push({
            blockTitle: spot.blockTitle || 'All Day',
            blockIndex: spot.blockIndex,
            spotsRemaining: spot.spotsRemaining,
          });
        });
      }
    }

    return blocks;
  };

  const getDisplayBlockTitle = (blockTitle?: string) =>
    (blockTitle || 'All Day').replace(/\s*drop\s*off$/i, '');

  // "Block 1" is the name the settings page fills in for a block the
  // processor never named — it only exists to tell several blocks on one day
  // apart. On a day with a single block it tells the person booking nothing,
  // so it isn't shown. The value still travels with the selection unchanged.
  const isUnnamedOnlyBlock = (
    blocks: Array<{ blockTitle: string }>,
    blockTitle: string,
  ) => blocks.length === 1 && isGeneratedBlockTitle(blockTitle);

  // Determine pip color based on capacity for a specific block
  const getPipColorForBlock = (spotsRemaining: number) => {
    // Producer view: green when enough capacity, grey otherwise (covers both
    // zero spots and "some but not enough" — red is too alarming for a
    // discovery surface).
    if (isProducerView) {
      return spotsRemaining >= selectedQuantity
        ? theme.palette.success.main
        : theme.palette.text.disabled;
    }

    // Processor view: detailed capacity levels
    // Grey: total capacity is zero (or negative)
    if (spotsRemaining <= 0) {
      return theme.palette.text.disabled;
    }

    // Red: selected quantity is more than available slots
    if (selectedQuantity > spotsRemaining) {
      return theme.palette.error.main;
    }

    // Yellow: enough slots but there are current bookings (capacity not at max)
    // This assumes that if spotsRemaining is less than total capacity, there are bookings
    // For now, we'll use yellow if there's capacity but it's limited
    if (spotsRemaining < selectedQuantity * 2) {
      return theme.palette.warning.main;
    }

    // Green: day has plenty of capacity
    return theme.palette.success.main;
  };

  // Check if all spots for a date are outside the scheduling window (call to schedule)
  const isOutsideWindowDate = (date: Moment) => {
    if (!capacityData?.calculateProcessorCapacity?.spotGroups) return false;
    const dateStr = date.format('M/DD/YYYY');
    const { spotGroups } = capacityData.calculateProcessorCapacity;
    let hasSpots = false;
    for (const groupKey of Object.keys(spotGroups)) {
      const group = spotGroups[groupKey] as { spots?: CapacitySpot[] };
      const matchingSpots = group.spots?.filter(
        (s: CapacitySpot) => s.date === dateStr,
      );
      if (matchingSpots?.length) {
        hasSpots = true;
        if (
          !matchingSpots.every((s: CapacitySpot) => s.isOutsideSchedulingWindow)
        ) {
          return false;
        }
      }
    }
    return hasSpots;
  };

  const getOutsideWindowNote = (date: Moment): string | undefined => {
    if (!capacityData?.calculateProcessorCapacity?.spotGroups) return undefined;
    const dateStr = date.format('M/DD/YYYY');
    const { spotGroups } = capacityData.calculateProcessorCapacity;
    for (const groupKey of Object.keys(spotGroups)) {
      const spot = (spotGroups[groupKey].spots as CapacitySpot[])?.find(
        (s) => s.date === dateStr && s.isOutsideSchedulingWindow,
      );
      if (spot?.outsideWindowNote) return spot.outsideWindowNote;
    }
    return undefined;
  };

  // Get all blocks for a date and their pip colors
  const getPipsForDate = (date: Moment) => {
    if (!capacityData?.calculateProcessorCapacity?.spotGroups) {
      return [];
    }

    const dateStr = date.format('M/DD/YYYY');
    const { spotGroups } = capacityData.calculateProcessorCapacity;
    const pips: Array<{ key: string; color: string; capacity: number }> = [];

    // Find all blocks for this date across all groups
    for (const groupKey of Object.keys(spotGroups)) {
      const group = spotGroups[groupKey] as { spots?: CapacitySpot[] };
      const matchingSpots = group.spots?.filter(
        (s: CapacitySpot) => s.date === dateStr,
      );
      if (matchingSpots) {
        matchingSpots.forEach((spot: CapacitySpot) => {
          // Outside-window spots always render as grey with real capacity numbers
          const color = spot.isOutsideSchedulingWindow
            ? theme.palette.text.disabled
            : getPipColorForBlock(spot.spotsRemaining);
          pips.push({
            key: `${groupKey}-${spot.blockIndex ?? spot.blockTitle ?? 'default'}`,
            color,
            capacity: spot.spotsRemaining,
          });
        });
      }
    }

    return pips;
  };

  const renderCalendarDays = () => {
    const startOfMonth = currentMonth.clone().startOf('month');
    const endOfMonth = currentMonth.clone().endOf('month');
    const startDate = startOfMonth.clone().startOf('week');
    const endDate = endOfMonth.clone().endOf('week');

    const days = [];
    const current = startDate.clone();

    while (current.isSameOrBefore(endDate)) {
      const dateClone = current.clone();
      const isToday = current.isSame(today, 'day');
      const isSelected = selectedDate && current.isSame(selectedDate, 'day');
      const isOtherMonth = !current.isSame(currentMonth, 'month');
      // For processor view, allow past dates; for producer view, enforce minDate
      const isDisabled = isProducerView && current.isBefore(minDate, 'day');
      const isOutsideWindow =
        !isProcessor &&
        !isOtherMonth &&
        !isDisabled &&
        isOutsideWindowDate(current);
      const pips = !isOtherMonth && !isDisabled ? getPipsForDate(current) : [];
      const isLowCapacity =
        pips.length > 0 &&
        pips.every((pip) => pip.color === theme.palette.text.disabled) &&
        !isOtherMonth;

      const cellKey = current.format('YYYY-MM-DD');

      const buildAvailabilityTooltip = (date: Moment): string => {
        const blocks = getBlocksForDate(date);
        if (!blocks.length) return '';
        const speciesLabel =
          species.charAt(0).toUpperCase() + species.slice(1).toLowerCase();
        const lines = blocks.map((b) =>
          (blocks.length === 1 && b.blockTitle === 'All Day') ||
          isUnnamedOnlyBlock(blocks, b.blockTitle)
            ? `${b.spotsRemaining}`
            : `${getDisplayBlockTitle(b.blockTitle)}: ${b.spotsRemaining}`,
        );
        return `${speciesLabel} — ${lines.join(' | ')}`;
      };

      const availabilityTooltip =
        !isOtherMonth && !isDisabled && pips.length > 0
          ? buildAvailabilityTooltip(dateClone)
          : '';

      const cell = (
        <DayCell
          data-testid={`date-cell-${cellKey}`}
          className={`
            ${isToday ? 'today' : ''}
            ${isSelected ? 'selected' : ''}
            ${isOtherMonth ? 'other-month' : ''}
            ${isDisabled ? 'disabled' : ''}
            ${isLowCapacity ? 'low-capacity' : ''}
            ${isOutsideWindow ? 'outside-window' : ''}
          `}
          onClick={() => {
            if (!isDisabled && !isOtherMonth && !isOutsideWindow) {
              // Check if there's only one block with capacity
              const blocks =
                getPipsForDate(dateClone).length > 0
                  ? getBlocksForDate(dateClone)
                  : [];

              const shouldAutoSelect = blocks.length === 1 && onBlockSelect;

              if (shouldAutoSelect) {
                const block = blocks[0];
                const hasCapacity =
                  isProcessor || block.spotsRemaining >= selectedQuantity;
                if (hasCapacity) {
                  // Store the date we're auto-selecting to prevent overlay from opening
                  const dateStr = dateClone.format('YYYY-MM-DD');
                  autoSelectedDateRef.current = dateStr;
                  // Auto-select single block - call both immediately
                  onDateSelect(dateClone);
                  onBlockSelect({
                    blockTitle: block.blockTitle,
                    blockIndex: block.blockIndex,
                    date: dateStr,
                  });
                  return;
                }
              }

              // Show block selection overlay only if multiple blocks or no auto-select
              // Clear any previous auto-selected date since this is a manual selection
              autoSelectedDateRef.current = null;
              onDateSelect(dateClone);
              setSelectedDateForBlocks(dateClone);
              setShowBlockSelection(true);
            }
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: '0.95rem', sm: '0.8rem' },
              lineHeight: 1,
            }}
          >
            {current.format('D')}
          </Typography>
          <PipContainer>
            {pips.map((pip) => (
              <Pip key={pip.key} pipColor={pip.color} compact={isMobile}>
                {isMobile ? null : pip.capacity}
              </Pip>
            ))}
          </PipContainer>
        </DayCell>
      );

      const outsideWindowNote = isOutsideWindow
        ? getOutsideWindowNote(dateClone)
        : undefined;

      let dayCellContent: ReactNode;
      if (isOutsideWindow) {
        dayCellContent = (
          <Tooltip
            key={cellKey}
            title={outsideWindowNote ?? 'Contact the processor to schedule'}
            placement="top"
            arrow
          >
            <span>{cell}</span>
          </Tooltip>
        );
      } else if (availabilityTooltip) {
        dayCellContent = (
          <Tooltip
            key={cellKey}
            title={availabilityTooltip}
            placement="top"
            arrow
          >
            <span>{cell}</span>
          </Tooltip>
        );
      } else {
        dayCellContent = <span key={cellKey}>{cell}</span>;
      }

      days.push(dayCellContent);

      current.add(1, 'day');
    }

    return days;
  };

  return (
    <Paper elevation={0} sx={{ p: isMobile ? 0 : 0.5, position: 'relative' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 0.5,
        }}
      >
        <Button
          ref={monthLabelRef}
          variant="text"
          startIcon={<KeyboardArrowDown />}
          sx={{
            fontWeight: 600,
            fontSize: '1.2rem',
            px: 1,
            py: 0.5,
            lineHeight: 1.2,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
          onClick={() => {
            setShowMonthSelector(true);
            setSelectorYear(currentMonth.year());
          }}
        >
          {currentMonth.format('MMMM YYYY')}
        </Button>
        <Box>
          <IconButton
            size="medium"
            sx={{ p: 0.5 }}
            aria-label="Previous month"
            onClick={() =>
              setCurrentMonth(currentMonth.clone().subtract(1, 'month'))
            }
          >
            <ChevronLeft fontSize="medium" />
          </IconButton>
          <IconButton
            size="medium"
            sx={{ p: 0.5 }}
            aria-label="Next month"
            onClick={() =>
              setCurrentMonth(currentMonth.clone().add(1, 'month'))
            }
          >
            <ChevronRight fontSize="medium" />
          </IconButton>
        </Box>
      </Box>

      {/* Month Selector Popover */}
      <Popover
        open={showMonthSelector}
        anchorEl={monthLabelRef.current}
        onClose={() => setShowMonthSelector(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box sx={{ p: 2, minWidth: 280 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <IconButton
              size="small"
              onClick={() => setSelectorYear(selectorYear - 1)}
            >
              <ChevronLeft fontSize="small" />
            </IconButton>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {selectorYear}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setSelectorYear(selectorYear + 1)}
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          </Box>
          <Grid container spacing={1}>
            {moment.months().map((month, index) => {
              const isCurrentMonth =
                currentMonth.month() === index &&
                currentMonth.year() === selectorYear;
              return (
                <Grid size={4} key={month}>
                  <Box
                    sx={{
                      p: 1,
                      textAlign: 'center',
                      cursor: 'pointer',
                      borderRadius: 1,
                      backgroundColor: isCurrentMonth
                        ? 'primary.main'
                        : 'transparent',
                      color: isCurrentMonth
                        ? 'primary.contrastText'
                        : 'text.primary',
                      transition: 'all 0.2s',
                      '&:hover': {
                        backgroundColor: isCurrentMonth
                          ? 'primary.dark'
                          : 'action.hover',
                      },
                    }}
                    onClick={() => {
                      setCurrentMonth(moment().year(selectorYear).month(index));
                      setShowMonthSelector(false);
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {month.slice(0, 3)}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Popover>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 0.2,
          mb: 0.3,
          backgroundColor: 'action.hover',
          borderRadius: 0.5,
          py: 0.5,
        }}
      >
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <Typography
            key={day}
            variant="caption"
            align="center"
            sx={{
              fontWeight: 'bold',
              color: 'text.secondary',
              fontSize: '0.6rem',
            }}
          >
            {day}
          </Typography>
        ))}
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          columnGap: 0.2,
          rowGap: isMobile ? 0.4 : 0.2,
        }}
      >
        {renderCalendarDays()}
      </Box>

      {/* Capacity Legend — hidden on mobile to save vertical space */}
      <Box
        sx={{
          display: isMobile ? 'none' : 'flex',
          gap: 2,
          justifyContent: 'center',
          py: 1,
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        {isProducerView ? (
          <>
            <LegendItem>
              <LegendPip pipColor={theme.palette.success.main} />
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                Available
              </Typography>
            </LegendItem>
            <LegendItem>
              <LegendPip pipColor={theme.palette.text.disabled} />
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                Insufficient Capacity
              </Typography>
            </LegendItem>
          </>
        ) : (
          <>
            <LegendItem>
              <LegendPip pipColor={theme.palette.success.main} />
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                Plenty of Capacity
              </Typography>
            </LegendItem>
            <LegendItem>
              <LegendPip pipColor={theme.palette.warning.main} />
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                Limited Capacity
              </Typography>
            </LegendItem>
            <LegendItem>
              <LegendPip pipColor={theme.palette.error.main} />
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                Over-Capacity
              </Typography>
            </LegendItem>
            <LegendItem>
              <LegendPip pipColor={theme.palette.text.disabled} />
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                Closed
              </Typography>
            </LegendItem>
          </>
        )}
      </Box>

      {showBlockSelection &&
        selectedDateForBlocks &&
        getBlocksForDate(selectedDateForBlocks).length > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(4px)',
              borderRadius: 1,
              border: 1,
              borderColor: 'divider',
              p: 2,
              zIndex: 10,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <CardHeader
              subheader="Select Time Slot"
              slotProps={{
                title: { variant: 'overline', lineHeight: 1.2 },
                subheader: { variant: 'subtitle2' },
              }}
              title={selectedDateForBlocks.format('MMMM D, YYYY')}
              avatar={
                <IconButton
                  size="small"
                  onClick={() => {
                    setShowBlockSelection(false);
                    setSelectedDateForBlocks(null);
                  }}
                >
                  <ChevronLeft />
                </IconButton>
              }
            />

            <Stack spacing={1} sx={{ flex: 1, overflowY: 'auto' }}>
              {getBlocksForDate(selectedDateForBlocks).map((block, _, all) => {
                const isSelected =
                  selectedBlock?.blockTitle === block.blockTitle &&
                  selectedBlock?.blockIndex === block.blockIndex;
                const hasCapacity =
                  isProcessor || block.spotsRemaining >= selectedQuantity;

                return (
                  <Card
                    key={`${block.blockTitle}-${block.blockIndex}`}
                    data-testid={`block-card-${block.blockIndex ?? block.blockTitle}`}
                    variant="outlined"
                    sx={{
                      cursor: hasCapacity ? 'pointer' : 'not-allowed',
                      backgroundColor: isSelected
                        ? 'action.selected'
                        : 'background.paper',
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      borderWidth: isSelected ? 2 : 1,
                      opacity: hasCapacity ? 1 : 0.6,
                      transition: 'all 0.2s',
                      '&:hover': hasCapacity
                        ? {
                            borderColor: 'primary.main',
                            backgroundColor: 'action.hover',
                          }
                        : {},
                    }}
                    onClick={() => {
                      if (hasCapacity && onBlockSelect) {
                        onBlockSelect({
                          blockTitle: block.blockTitle,
                          blockIndex: block.blockIndex,
                          date: selectedDateForBlocks.format('YYYY-MM-DD'),
                        });
                      }
                    }}
                  >
                    <CardContent
                      sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flex: 1,
                          }}
                        >
                          <Checkbox
                            checked={isSelected}
                            disabled={!hasCapacity}
                            sx={{ p: 0 }}
                          />
                          {!isUnnamedOnlyBlock(all, block.blockTitle) && (
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 600 }}
                            >
                              {getDisplayBlockTitle(block.blockTitle)}
                            </Typography>
                          )}
                        </Box>
                        <Typography
                          variant="body2"
                          color={hasCapacity ? 'success.main' : 'error.main'}
                          sx={{ fontWeight: 500 }}
                        >
                          {block.spotsRemaining} spots remaining
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
              {renderUnavailableDayContent &&
                getBlocksForDate(selectedDateForBlocks).some(
                  (b) => !isProcessor && b.spotsRemaining < selectedQuantity,
                ) &&
                renderUnavailableDayContent(selectedDateForBlocks)}
            </Stack>
          </Box>
        )}
    </Paper>
  );
}
